import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Link, Snackbar } from "@mui/material";
import { Button, Dialog, Spinner } from "@/components/ui";
import { useAuthedApi } from "@/api/useAuthedApi";
import type { SaveResult, SaveScopeOption } from "@/api/conversationKnowledgeApi";

function optionKey(o: SaveScopeOption): string {
  return o.scope === "department" ? `department:${o.dept_id}` : o.scope;
}

const KNOWLEDGE_PATH = "/workspace/knowledge";

/** "Save to knowledge" for one conversation: a scope picker, then the save
 *  (Path 2, spec §5.3), then a toast with the server's sentence and — when
 *  it was kept — a link to the stored conversation. The toast outlives the
 *  dialog, so this stays mounted and opens when `sessionId` is set. */
export function SaveConversationDialog({
  sessionId,
  onClose,
}: {
  sessionId: string | null;
  onClose: () => void;
}) {
  const { conversationKnowledge } = useAuthedApi();
  // The server's own list (GET /scopes), so the picker offers exactly what
  // the save route accepts. null while loading.
  const [options, setOptions] = useState<SaveScopeOption[] | null>(null);
  const [picked, setPicked] = useState("personal");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);
  const open = sessionId != null;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPicked("personal");
    setError(null);
    setOptions(null);
    conversationKnowledge.scopes().then(
      (scopes) => {
        if (cancelled) return;
        if (scopes === null) {
          setOptions([]);
          setError("Saving conversations to knowledge is not enabled.");
        } else {
          setOptions(scopes);
        }
      },
      (e) => {
        if (cancelled) return;
        console.warn("save scopes read failed", e);
        setOptions([]);
        setError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, conversationKnowledge]);

  const save = async () => {
    const option = options?.find((o) => optionKey(o) === picked);
    if (!sessionId || !option) return;
    setSaving(true);
    setError(null);
    try {
      const res = await conversationKnowledge.save(sessionId, option.scope, option.dept_id);
      setResult(res);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const kept = result?.outcome === "kept" && result.document_id != null;
  const severity = !result ? "info" : !result.ok ? "error" : kept ? "success" : "info";

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? () => {} : onClose}
        title="Save to knowledge"
        scope="sp-save_knowledge_dialog"
      >
        <div className="px-6 pb-5 space-y-4">
          <p className="text-[13px] text-ink-muted m-0">
            The assistant keeps a summary of this conversation and anything useful it learned.
            Who should it be available to?
          </p>
          <fieldset className="space-y-1.5 border-0 p-0 m-0" disabled={saving}>
            <legend className="sr-only">Who can use it</legend>
            {options === null && <Spinner size={16} />}
            {options?.map((o) => (
              <label
                key={optionKey(o)}
                className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13.5px] text-ink cursor-pointer hover:bg-hover"
              >
                <input
                  type="radio"
                  name="save-knowledge-scope"
                  value={optionKey(o)}
                  checked={picked === optionKey(o)}
                  onChange={() => setPicked(optionKey(o))}
                  data-ph="select"
                />
                {o.label}
              </label>
            ))}
          </fieldset>
          {error && (
            <p role="alert" className="text-[13px] text-danger m-0">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" data-ph="cancel" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              data-ph="save"
              onClick={() => void save()}
              disabled={saving || !options?.some((o) => optionKey(o) === picked)}
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size={14} className="text-white" /> Saving…
                </span>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
      <Snackbar
        open={!!result}
        autoHideDuration={8000}
        onClose={() => setResult(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={severity}
          onClose={() => setResult(null)}
          role="status"
          data-ph-scope="sp-save_knowledge_toast"
          action={
            kept ? (
              <Link
                component={RouterLink}
                to={`${KNOWLEDGE_PATH}?view=conversations&conversation=${result!.document_id}`}
                onClick={() => setResult(null)}
                data-ph="open_knowledge"
                color="inherit"
                sx={{ alignSelf: "center" }}
              >
                View it
              </Link>
            ) : undefined
          }
        >
          {result?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
